<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api, extractError } from '@/api/client'
import { DEFAULT_DIGISELLER_SELLER } from '@/lib/digisellerLoader'

interface SubscriptionOption {
  id: number
  label: string
  priceModifierPercent: number
  default: boolean
  available: boolean
}

interface PaymentMethod {
  name: string
  code: string
}

interface ProductPayload {
  id: number
  name: string
  priceRub: number
  payUrl: string
  available: boolean
  digisellerAgentId: string
  subscriptionOptions?: SubscriptionOption[]
  subscriptionGroup?: { id: number; name: string; label: string }
  confirmOption?: { id: number; label: string; required: boolean }
  paymentMethods?: PaymentMethod[]
}

const route = useRoute()
const loading = ref(true)
const error = ref('')
const product = ref<ProductPayload | null>(null)
const selectedOptionId = ref<number | null>(null)
const confirmChecked = ref(false)

const productId = computed(() => String(route.params.productId || ''))
const agentId = computed(() => {
  const q = route.query.ai
  if (typeof q === 'string' && q) return q
  return product.value?.digisellerAgentId || DEFAULT_DIGISELLER_SELLER
})

const availableOptions = computed(
  () => product.value?.subscriptionOptions?.filter((o) => o.available !== false) ?? [],
)

const selectedOption = computed(
  () => availableOptions.value.find((o) => o.id === selectedOptionId.value) ?? null,
)

const displayPrice = computed(() => {
  if (!product.value) return 0
  const base = product.value.priceRub
  const mod = selectedOption.value?.priceModifierPercent ?? 0
  return Math.round(base * (1 + mod / 100))
})

function formatRub(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function buildPayUrl(paymentCode?: string): string {
  if (!product.value?.payUrl) return ''
  const url = new URL(product.value.payUrl)
  if (selectedOption.value && product.value.subscriptionGroup?.name) {
    url.searchParams.set(product.value.subscriptionGroup.name, String(selectedOption.value.id))
  }
  if (agentId.value) {
    url.searchParams.set('ai', agentId.value)
  }
  if (paymentCode) {
    url.searchParams.set('type', paymentCode)
  }
  return url.toString()
}

function canPay(): boolean {
  if (!product.value?.available) return false
  if (product.value.confirmOption?.required && !confirmChecked.value) return false
  return !!buildPayUrl()
}

function openPay(paymentCode?: string) {
  if (!canPay()) return
  const url = buildPayUrl(paymentCode)
  if (!url) return
  window.open(url, '_blank', 'noopener')
}

async function loadProduct() {
  loading.value = true
  error.value = ''
  try {
    const ai = typeof route.query.ai === 'string' ? route.query.ai : ''
    const { data } = await api.get<{ success: boolean; product: ProductPayload; message?: string }>(
      `/api/user/digiseller-product/${encodeURIComponent(productId.value)}`,
      ai ? { params: { ai } } : undefined,
    )
    if (!data.success || !data.product) {
      error.value = data.message || 'Товар не найден'
      return
    }
    product.value = data.product
    document.title = `${data.product.name} — покупка`
    const opts = data.product.subscriptionOptions?.filter((o) => o.available !== false) ?? []
    const def = opts.find((o) => o.default) ?? opts[0]
    selectedOptionId.value = def?.id ?? null
  } catch (e) {
    error.value = extractError(e)
  } finally {
    loading.value = false
  }
}

onMounted(() => void loadProduct())
</script>

<template>
  <div class="purchase-page">
    <div class="purchase-shell">
      <a href="/" class="purchase-back">
        <i class="bi bi-arrow-left" /> Назад в кабинет
      </a>

      <div v-if="loading" class="purchase-card py-16 text-center text-muted">Загрузка…</div>
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

      <div v-else-if="product" class="purchase-card">
        <h1 class="purchase-title">{{ product.name }}</h1>

        <div class="purchase-price">{{ formatRub(displayPrice) }}</div>
        <p v-if="!product.available" class="purchase-unavailable">Нет в наличии</p>

        <div v-if="availableOptions.length" class="purchase-section">
          <p class="purchase-label">
            {{ product.subscriptionGroup?.label || 'Срок подписки' }}
          </p>
          <div class="purchase-options">
            <label
              v-for="opt in availableOptions"
              :key="opt.id"
              class="purchase-option"
              :class="{ 'purchase-option--active': selectedOptionId === opt.id }"
            >
              <input v-model="selectedOptionId" type="radio" class="sr-only" :value="opt.id" />
              <span>{{ opt.label }}</span>
            </label>
          </div>
        </div>

        <label v-if="product.confirmOption" class="purchase-confirm">
          <input v-model="confirmChecked" type="checkbox" />
          <span>{{ product.confirmOption.label }}</span>
        </label>

        <div v-if="product.paymentMethods?.length" class="purchase-section">
          <p class="purchase-label">Способ оплаты</p>
          <div class="purchase-payments">
            <button
              v-for="method in product.paymentMethods"
              :key="method.code || method.name"
              type="button"
              class="purchase-pay-btn"
              :disabled="!canPay()"
              @click="openPay(method.code || undefined)"
            >
              {{ method.name }}
            </button>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-primary purchase-submit"
          :disabled="!canPay()"
          @click="openPay()"
        >
          Перейти к оплате
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.purchase-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: var(--color-surface, #fafafa);
}

.purchase-shell {
  width: 100%;
  max-width: 420px;
}

.purchase-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--color-white-dark, #888ea8);
  transition: color 0.2s;
}

.purchase-back:hover {
  color: var(--color-primary, #4361ee);
}

.purchase-card {
  border-radius: 12px;
  background: #fff;
  padding: 28px 24px;
  box-shadow: 0 4px 24px rgba(14, 23, 38, 0.08);
}

.purchase-title {
  text-align: center;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.4;
  color: var(--color-black, #0e1726);
}

.purchase-price {
  margin-top: 16px;
  text-align: center;
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-primary, #4361ee);
}

.purchase-unavailable {
  margin-top: 8px;
  text-align: center;
  font-size: 14px;
  color: var(--color-danger, #e7515a);
}

.purchase-section {
  margin-top: 24px;
}

.purchase-label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-dark, #3b3f5c);
}

.purchase-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.purchase-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--color-white-light, #e0e6ed);
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: border-color 0.2s, background 0.2s;
}

.purchase-option:hover {
  border-color: var(--color-primary, #4361ee);
}

.purchase-option--active {
  border-color: var(--color-primary, #4361ee);
  background: var(--color-primary-light, #eaf1ff);
}

.purchase-confirm {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
  font-size: 14px;
  cursor: pointer;
}

.purchase-confirm input {
  margin-top: 3px;
  flex-shrink: 0;
}

.purchase-payments {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.purchase-pay-btn {
  padding: 10px 12px;
  border: 1px solid var(--color-white-light, #e0e6ed);
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-black, #0e1726);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.purchase-pay-btn:hover:not(:disabled) {
  border-color: var(--color-primary, #4361ee);
  background: var(--color-primary-light, #eaf1ff);
}

.purchase-pay-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.purchase-submit {
  width: 100%;
  margin-top: 24px;
  padding-top: 12px;
  padding-bottom: 12px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
